import React, { useEffect, useState, useRef } from "react";
import Loader from "../Loader/Loader";

function prettyBibtexAligned(input, maxLineLength = 80) {
  if (!input) return "";

  let str = input
    .replace(/\r?\n|\r/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const match = str.match(/^(@\w+)\s*\{([\s\S]*)\}$/);
  if (!match) return input;

  const entryType = match[1];
  const body = match[2];

  const parts = [];
  let current = "";
  let braceLevel = 0;

  for (let char of body) {
    if (char === "{") braceLevel++;
    if (char === "}") braceLevel--;

    if (char === "," && braceLevel === 0) {
      parts.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  if (current.trim()) parts.push(current.trim());

  const [citationKey, ...fields] = parts;

  const parsedFields = fields.map((f) => {
    const idx = f.indexOf("=");
    return {
      key: f.slice(0, idx).trim(),
      value: f.slice(idx + 1).trim(),
    };
  });

  const maxKeyLength = Math.max(...parsedFields.map((f) => f.key.length));

  let result = `${entryType}{${citationKey},\n`;

  result += parsedFields
    .map(({ key, value }) => {
      const paddedKey = key.padEnd(maxKeyLength, " ");
      const prefix = `  ${paddedKey} = `;
      const indent = " ".repeat(prefix.length);

      // Wrap value manually
      const words = value.split(" ");
      let lines = [];
      let currentLine = "";

      words.forEach((word) => {
        if ((currentLine + " " + word).trim().length > maxLineLength) {
          lines.push(currentLine.trim());
          currentLine = word;
        } else {
          currentLine += " " + word;
        }
      });

      if (currentLine) lines.push(currentLine.trim());

      // First line with prefix, rest aligned
      return [
        prefix + lines[0],
        ...lines.slice(1).map((line) => indent + line),
      ].join("\n");
    })
    .join(",\n");

  result += "\n}";

  return result;
}

function formatReadableCitation(input, maxLineLength = 100) {
  if (!input) return "";

  let str = input
    .replace(/\r?\n|\r/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  // Smart break points (common citation separators)
  const breakPoints = [
    /\. /, // sentence end
    /, https?:\/\//, // before links
    /, vol\./i,
    /, no\./i,
    /, pp\./i,
    /\) /, // after year
  ];

  let parts = [str];

  // Split step by step using breakpoints
  breakPoints.forEach((regex) => {
    parts = parts.flatMap((part) => {
      return part.split(regex).map((p, i, arr) => {
        if (i < arr.length - 1) {
          return p + part.match(regex)?.[0];
        }
        return p;
      });
    });
  });

  // Wrap long lines
  const wrapped = [];

  parts.forEach((part) => {
    const words = part.trim().split(" ");
    let line = "";

    words.forEach((word) => {
      if ((line + " " + word).trim().length > maxLineLength) {
        wrapped.push(line.trim());
        line = word;
      } else {
        line += " " + word;
      }
    });

    if (line) wrapped.push(line.trim());
  });

  return wrapped.join("\n");
}

function formatByType(type, text, maxLineLength) {
  if (type === "BibTeX") {
    return prettyBibtexAligned(text, maxLineLength);
  }
  if (type === "RIS") {
    return text;
  }
  return text;
  // return formatReadableCitation(text);
}

export default function CitationModal({ show, onClose, citationData = {} }) {
  const [active, setActive] = useState("BibTeX");
  const [loading, setLoading] = useState(false);
  const modalRef = useRef(null);
  const [maxLineLength, setMaxLineLength] = useState(80);

  useEffect(() => {
    if (modalRef.current) {
      const width = modalRef.current.offsetWidth;
      setMaxLineLength(width === 800 ? 80 : 40);
    }
  }, [modalRef.current]);

  if (!show) return null;
  // if (!citationData) return null;

  const formats = Object.keys(citationData?.citations || {});

  const handleTabClick = (format) => {
    setLoading(true);
    setActive(format);

    // fake loader (like your screenshot)
    setTimeout(() => setLoading(false), 500);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(citationData.citations[active]);
  };

  const handleDownload = () => {
    const blob = new Blob([citationData.citations[active]], {
      type: "text/plain",
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${active}.txt`;
    a.click();

    URL.revokeObjectURL(url);
  };

  return (
    <>
      {/* Backdrop */}
      <div className="modal-backdrop fade show"></div>

      {/* Modal */}
      <div className="modal show d-block" onClick={onClose} tabIndex="-1">
        <div
          ref={modalRef}
          className="modal-dialog modal-dialog-top modal-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            {/* Header */}
            <div className="modal-header">
              <h5 className="modal-title fs-6">
                Citation Information for:{" "}
                {citationData && (
                  <a
                    href={`https://doi.org/${citationData.doi}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-theme"
                  >
                    {citationData.doi}
                  </a>
                )}
              </h5>
              <button className="btn-close" onClick={onClose}></button>
            </div>

            {/* Body */}
            <div className="modal-body">
              {citationData ? (
                <>
                  {/* Tabs */}
                  <ul className="nav nav-tabs mb-3">
                    {formats.map((format) => (
                      <li className="nav-item" key={format}>
                        <button
                          className={`nav-link text-theme ${
                            active === format ? "active text-dark" : ""
                          }`}
                          onClick={() => handleTabClick(format)}
                        >
                          {format}
                        </button>
                      </li>
                    ))}
                  </ul>

                  {/* Content Box */}
                  <div
                    className="border rounded p-3 bg-light position-relative"
                    style={{ minHeight: "150px" }}
                  >
                    {/* Loader */}
                    {loading && (
                      <div className="position-absolute top-50 start-50 translate-middle">
                        <div className="spinner-border text-theme"></div>
                      </div>
                    )}

                    {!loading && (
                      <>
                        {/* <div className="d-flex justify-content-end mb-2">
                      <button
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={handleCopy}
                      >
                        Copy
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleDownload}
                      >
                        Download
                      </button>
                      </div> */}

                        <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                          {formatByType(
                            active,
                            citationData.citations[active],
                            maxLineLength,
                          )}
                        </pre>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <Loader />
              )}
            </div>
            {/* Footer */}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
